import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";
import {
  AcceptStaffInvitationRequest,
  InviteRestaurantStaffRequest,
  RestaurantStaffDto,
  UpdateRestaurantStaffRequest,
} from "../types/staff";

type ApiResponseV2<T> = {
  success: boolean;
  message: string | null;
  data: T;
};

type ApiResponse = {
  success: boolean;
  message: string | null;
};

export const staffKeys = {
  list: (organizationId: string) =>
    ["organization", organizationId, "staff"] as const,
  detail: (id: string) => ["organization", "staff", id] as const,
};

// GET /web/staff?organization_id={id}
export const useOrganizationStaff = (organizationId: string | null | undefined) =>
  useQuery({
    queryKey: staffKeys.list(organizationId ?? ""),
    queryFn: async (): Promise<RestaurantStaffDto[]> => {
      const params = new URLSearchParams({ organization_id: organizationId! });
      const res = await fetch(`${API_URL}/staff?${params}`, {
        credentials: "include",
      });
      const body: ApiResponseV2<RestaurantStaffDto[]> = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to fetch staff");
      return body.data;
    },
    enabled: !!organizationId,
  });

// GET /web/staff/{id}
export const useStaffMember = (id: string | null | undefined) =>
  useQuery({
    queryKey: staffKeys.detail(id ?? ""),
    queryFn: async (): Promise<RestaurantStaffDto> => {
      const res = await fetch(`${API_URL}/staff/${id}`, {
        credentials: "include",
      });
      const body: ApiResponseV2<RestaurantStaffDto> = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to fetch staff member");
      return body.data;
    },
    enabled: !!id,
  });

// PUT /web/staff/{id}
export const useUpdateStaffMember = (
  onSuccess?: (staff: RestaurantStaffDto) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateRestaurantStaffRequest;
    }): Promise<RestaurantStaffDto> => {
      const res = await fetch(`${API_URL}/staff/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body: ApiResponseV2<RestaurantStaffDto> = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to update staff member");
      return body.data;
    },
    onSuccess: (staff) => {
      queryClient.invalidateQueries({ queryKey: staffKeys.detail(staff.id) });
      queryClient.invalidateQueries({
        queryKey: staffKeys.list(staff.organizationId),
      });
      onSuccess?.(staff);
    },
  });
};

// POST /web/staff/invite
export const useInviteStaff = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: InviteRestaurantStaffRequest
    ): Promise<void> => {
      const res = await fetch(`${API_URL}/staff/invite`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body: ApiResponse = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to send invitation");
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: staffKeys.list(variables.organizationId),
      });
      onSuccess?.();
    },
  });
};

// POST /web/staff/invitation/accept (public — gated only by the token)
export const useAcceptStaffInvitation = (onSuccess?: () => void) =>
  useMutation({
    mutationFn: async (
      payload: AcceptStaffInvitationRequest
    ): Promise<void> => {
      const res = await fetch(`${API_URL}/staff/invitation/accept`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body: ApiResponse = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to accept invitation");
    },
    onSuccess,
  });

// DELETE /web/staff/{id}
export const useDeleteStaffMember = (
  organizationId: string | null | undefined,
  onSuccess?: () => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_URL}/staff/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const body: ApiResponse = await res.json();
      if (!body.success) throw new Error(body.message ?? "Failed to delete staff member");
    },
    onSuccess: () => {
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: staffKeys.list(organizationId),
        });
      }
      onSuccess?.();
    },
  });
};
